import React, { useState, useEffect } from 'react';
import styles from './access.module.css';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import useSWR from 'swr';
import { AlertColor } from '@mui/material';
import { serverEndPoint } from '../ServerCall/EnvironmentVariables';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import '../allPages.css';
import { fontSize, palette } from '../..';
import ManageAccessHeader from './ManageAccessHeader';
import AccessForContentTable from './AccessForContentTable';
import AddNewAccessModal from './AddNewAccessModal';
import UserDeleteConfirmation from './UserDeleteConfirmation';
import { TAlert } from '../../types/types';

const fetcher = (url: string) =>
  axios
    .get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    })
    .then((res) => res.data);

type SelectedUser = {
  id: string;
  name: string;
  emailId: string;
  isGroup: boolean;
  role: 'owner' | 'moderator' | 'creator' | 'custom_creator' | 'player';
};

type User = SelectedUser & {
  customPermissions?: [
    {
      contentTypeId: number;
      privilegeId: number;
    },
  ];
};

const AccessforContents = () => {
  const [isModalOpen, setIsModalOpen] = useState<any>(false);
  const [modalContent, setModalContent] = useState<any>('User');
  const [selectedUser, setSelectedUser] = useState<User | SelectedUser | null>(null);
  const [addedUser, setAddedUser] = useState<null | User>(null);
  const contentId = useParams().parentId;
  const parentId = useLocation().state.parentId;
  const [originalAccessList, setOriginalAccessList] = useState<any>([]);
  const [customPermissions, setCustomPermissions] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState<any>('');
  const [showUserDeleteConfirmation, setShowUserDeleteConfirmation] = useState(false);
  const [user_GroupId, setUser_GroupID] = useState<any>();
  const [user_GroupName, setUser_GroupName] = useState<any>();
  const [isInGroup, setIsInGroup] = useState(false);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isLoading, setIsLoading] = useState<any>(true);
  const [sortField, setSortField] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'name', direction: 'asc' });
  const [rowInEditMode, setRowInEditMode] = useState<null | User>(null);

  let roles: any[] = [
    { id: 3, value: 'owner', displayValue: 'Owner' },
    { id: 4, value: 'moderator', displayValue: 'Moderator' },
    { id: 5, value: 'creator', displayValue: 'Creator' },
    { id: 7, value: 'player', displayValue: 'Player' },
    { id: 6, value: 'custom_creator', displayValue: 'Custom Creator' },
  ];
  let contentTypes: any[] = [
    { id: 1, value: 'subFolder', displayValue: 'Sub Folder' },
    { id: 2, value: 'dbConnection', displayValue: 'DB Connection' },
    { id: 3, value: 'dataset', displayValue: 'Data Set' },
    { id: 4, value: 'flatfile', displayValue: 'Data File' },
    { id: 5, value: 'playbook', displayValue: 'Playbook' },
  ];

  const [alert, setAlert] = useState<null | TAlert>(null);

  const { data: workspaceContent } = useSWR(`${serverEndPoint}workspace/${parentId}`, fetcher);

  const currentContent = workspaceContent?.find((workspace: any) => workspace.id === contentId);
  const {
    data: accessList = [],
    mutate: mutateAccessList,
    isValidating,
  } = useSWR(
    `${serverEndPoint}content/access?workspaceId=${parentId}&contentId=${contentId}&contentType=${currentContent?.contentType}`,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const handleNewButtonClick = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    // setIsLoading(false);
    if (!addedUser && accessList.length > 0 && originalAccessList.length === 0) {
      setOriginalAccessList(JSON.parse(JSON.stringify(accessList)));
      const permissions: any = {};
      accessList.forEach((access: any) => {
        permissions[access.id] = {};
        permissions[access.id][currentContent.contentType] = access.limit;
      });
      setCustomPermissions(permissions); // Set the customPermissions state based on accessList
    }
    setIsLoading(isValidating);
  }, [accessList]);

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent('');
    setSearchQuery('');
    setSelectedUser(null);
  };

  const handleAdd = () => {
    const updatedAccessList = [...accessList];

    const existingUser = updatedAccessList.find((item) => item.userGroupId === selectedUser?.id);
    if (!existingUser) {
      updatedAccessList.push({
        id: selectedUser?.id,
        name: selectedUser?.name,
        role: 'custom_creator',
        isGroup: selectedUser?.isGroup,
      });
      if (selectedUser) {
        const rowInfo = {
          id: selectedUser.id,
          name: selectedUser.name,
          emailId: selectedUser.emailId,
          isGroup: selectedUser.isGroup,
          role: 'custom_creator' as 'custom_creator',
        };

        setRowInEditMode(rowInfo);
        setAddedUser(selectedUser);
      }
    }

    mutateAccessList(updatedAccessList, false);
    closeModal();
  };

  const showAlert = (message: string, severity: AlertColor) => {
    setTestMessage(message);
    setSeverity(severity);
    setOpenAlert(true);
    setTimeout(() => {
      setOpenAlert(false);
    }, 3000);
  };

  const handleDeleteUser = async () => {
    const token = localStorage.getItem('accessToken');
    const userAdded = addedUser?.id === user_GroupId;
    const userOrGroup = accessList.find((item: any) => item.id === user_GroupId);
    if (userAdded) {
      //For deleting not saved but Added User
      let updatedAccessList = [...accessList];
      updatedAccessList = updatedAccessList.filter((item) => item.id !== user_GroupId);
      mutateAccessList(updatedAccessList, false);
      setRowInEditMode(null)
      setOriginalAccessList(JSON.parse(JSON.stringify(updatedAccessList)));
      setAddedUser(null);
      showAlert(`${userOrGroup.name} deleted Successfully`, 'success');
      setShowUserDeleteConfirmation(false);
    } else {
      try {
        setIsLoading(true);
        const userOrGroup = accessList.find((item: any) => item.id === user_GroupId); // If roleId or roleName are not available in accessList, fetch them dynamically
        let roleId;

        if (userOrGroup && userOrGroup.role) {
          roleId = roles.find((item: any) => item.value === userOrGroup.role)?.id;
        } else {
          // Default roleId if not available
          roleId = 7; // Assuming "player" as default
        }
        await axios.delete(`${serverEndPoint}content/access-deletion?workspaceId=${parentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            // "X-TENANT-ID": "silzila",
          },
          data: [
            {
              granteeId: user_GroupId,
              isGroup: isInGroup,
              roleId: roleId,
              customPermissions: [
                {
                  contentId: contentId,
                  contentTypeId: currentContent.contentType,
                },
              ],
            },
          ],
        });
        let updatedAccessList = [...accessList];
        updatedAccessList = updatedAccessList.filter((item) => item.id !== user_GroupId);
        mutateAccessList(updatedAccessList, false);
        setRowInEditMode(null);
        setOriginalAccessList(JSON.parse(JSON.stringify(updatedAccessList)));
        console.log('Access deleted');
        showAlert(`${userOrGroup.name} deleted Successfully`, 'success');
      } catch (error) {
        console.error('Error deleting access:', error);
        showAlert('Error deleting user. Try Again', 'error');
      } finally {
        setIsLoading(false);
        setShowUserDeleteConfirmation(false);
      }
    }
  };

  const Functions = () => {
    if (rowInEditMode) {
      setAlert({
        message: 'There are unsaved changes. Can not proceed adding new user.',
        severity: 'error',
      });
      return;
    }

    handleNewButtonClick();
    setModalContent('User');
  };
  const getImageSrc = (contentType: any) => {
    switch (contentType) {
      case 1:
        return '/folder_work.png';
      case 2:
        return '/database_icon.svg';
      case 3:
        return '/ds.svg';
      case 4:
        return '/flat_file_icon.svg';
      case 5:
        return '/playbook_icon.svg';
      default:
        return '/default_icon.svg';
    }
  };
  const renderImage = (content: any) => {
    const imageSrc = getImageSrc(content.contentType);
    return (
      <img
        src={imageSrc}
        alt={content.name}
        style={{
          width: '24px',
          height: '24px',
          objectFit: 'contain',
        }}
      />
    );
  };

  return (
    <div className="access-container" style={{ height: '100%', padding: '0 auto' }}>
      <div style={{ height: '8.6rem', overflowY: 'hidden' }}>
        <ManageAccessHeader />
        <div className="heading">
          {currentContent ? (
            <>
              {renderImage(currentContent)}
              <h3 style={{ marginLeft: '6px', marginTop: '0' }}>{currentContent?.name}</h3>
            </>
          ) : (
            <img
              src="/folder_work.png"
              style={{ width: '28px', height: '28px' }}
              alt="Folder Icon"
            ></img>
          )}
        </div>
        <div
          className="subworkspace-action-buttons"
          style={{ marginTop: '1.45rem', height: '2rem' }}
        >
          <button
            className={`${styles.createWorkspace} create-workspace`}
            onClick={Functions}
            style={{
              fontSize: fontSize.medium,
              padding: '0.5rem 1rem',
              cursor: 'pointer',
            }}
          >
            <img
              className={`${styles.adminAddIconDefault} addIconDefault`}
              src="/add_purple.png"
              alt="Add Users Logo"
              style={{ width: '16px', height: '16px' }}
            />
            <img
              className={`${styles.adminAddIconHover} addIconHover`}
              src="/add_white.png"
              alt="Add Users Logo"
              style={{ width: '16px', height: '16px' }}
            />
            Add
          </button>
        </div>
      </div>
      <div className="access-table_loader_wrapper">
        {isLoading ? (
          <div
            className="loading-container"
            style={{
              height: '100%',
              width: '100%',
              border: '1px solid white',
              overflowY: 'auto',
              overflowX: 'hidden',
              marginTop: '0.5rem',
            }}
          >
            <div className="user-spinner"></div>
          </div>
        ) : accessList.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <div
              className="loading-default-text"
              style={{
                fontSize: fontSize.medium,
                color: palette.primary.contrastText,
              }}
            >
              Currently, no user/group have access to this particular{' '}
              {contentTypes[currentContent?.contentType - 1]?.displayValue}.
              <div>Please add user/groups via Add button.</div>
            </div>
          </div>
        ) : (
          // style for 'save_reload_table_wrapper' is in allPages.css
          <div className="save_reload_table_wrapper">
            <div
              className={`${styles.accesstableContainer} accesstableContainer`}
              style={{
                marginBottom: '0.2rem',
                height: 'calc(100% - 4.6rem)',
              }}
            >
              <AccessForContentTable
                addedUser={addedUser}
                setAddedUser={setAddedUser}
                originalAccessList={originalAccessList}
                rowInEditMode={rowInEditMode}
                setRowInEditMode={setRowInEditMode}
                contentTypes={contentTypes}
                currentContent={currentContent}
                accessList={accessList}
                customPermissions={customPermissions}
                setShowUserDeleteConfirmation={setShowUserDeleteConfirmation}
                setUser_GroupID={setUser_GroupID}
                setUser_GroupName={setUser_GroupName}
                setIsInGroup={setIsInGroup}
                setCustomPermissions={setCustomPermissions}
                mutateAccessList={mutateAccessList}
              />
            </div>
          </div>
        )}
      </div>
      {showUserDeleteConfirmation ? (
        <UserDeleteConfirmation
          showUserDeleteConfirmation={showUserDeleteConfirmation}
          isInGroup={isInGroup}
          user_GroupName={user_GroupName}
          setShowUserDeleteConfirmation={setShowUserDeleteConfirmation}
          handleDeleteUser={handleDeleteUser}
        />
      ) : null}

      <AddNewAccessModal
        accessList={accessList}
        isModalForContentAccess={true}
        addedUser={addedUser}
        modalContent={modalContent}
        searchQuery={searchQuery}
        sortField={sortField}
        selectedUser={selectedUser}
        isModalOpen={isModalOpen}
        setSearchQuery={setSearchQuery}
        setSortField={setSortField}
        setModalContent={setModalContent}
        setSelectedUser={setSelectedUser}
        handleAdd={handleAdd}
        closeModal={closeModal}
      />

      {alert && (
        <NotificationDialog
          openAlert={!!alert}
          severity={alert.severity}
          testMessage={alert.message}
          onCloseAlert={() => setAlert(null)}
        />
      )}

      <NotificationDialog
        openAlert={openAlert}
        severity={severity}
        testMessage={testMessage}
        onCloseAlert={() => {
          setOpenAlert(false);
          setTestMessage('');
        }}
      />
    </div>
  );
};
export default AccessforContents;
